import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'wouter';
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth0 } from '@auth0/auth0-react';
import AddToHubButton from '@/components/hub/add-to-hub-button';
import Header from '@/components/layout/header';
import HaasTooltip from '@/components/onboarding/haas-tooltip';
import { FEATURE_TOOLTIPS } from '@/components/onboarding/feature-tooltips';
import NavigationFooter from '@/components/layout/navigation-footer';
import { Button } from '@/components/ui/button';
import { Share2, Edit, Trash2, Clock } from 'lucide-react';
import ResourceShareDialog from '@/components/share/resource-share-dialog';
import type { ResourceDescriptor } from '@/components/share/resource-share';
import TimerForm from '@/components/timer/TimerForm';
import CircularTimer from '@/components/timer/CircularTimer';
import RectangularTimerFixed from '@/components/timer/RectangularTimerFixed';
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useTimerStore } from '@/store/useTimerStore';
import { useFetchWithAuth } from '@/lib/fetchWithAuth';
import { useCurrentHub } from '@/lib/hooks/use-current-hub';
import { useHubWriteAccess } from '@/hooks/use-hub-write-access';
import {
  acknowledgeTimerCompletion,
  getNormalTimerCompletionPath,
  getTimerCompletionKey,
  hasAcknowledgedTimerCompletion,
  isValidTimerCompletionResource,
} from '@/lib/timer-completion';

function SimpleButton({ onClick, children, variant = 'default', ...rest }: { onClick?: () => void; children: React.ReactNode; variant?: 'default' | 'outline'; [key: string]: any }) {
  const baseStyle = "px-4 py-2 rounded-md font-medium transition-colors";
  const styles = {
    default: `${baseStyle} bg-blue-600 text-white hover:bg-blue-700`,
    outline: `${baseStyle} border border-blue-600 text-blue-600 hover:bg-blue-50`
  };
  
  return (
    <button 
      onClick={onClick} 
      className={styles[variant]}
      {...rest}
    >
      {children}
    </button>
  );
}

function CareViewToggle({ 
  initialValue, 
  onToggle 
}: { 
  initialValue: boolean; 
  onToggle: (checked: boolean) => void;
}) {
  const [localChecked, setLocalChecked] = useState(initialValue);
  
  useEffect(() => {
    setLocalChecked(initialValue);
  }, [initialValue]);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.checked;
    setLocalChecked(newValue);
    onToggle(newValue);
  };
  
  return (
    <label className="relative inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        checked={localChecked}
        onChange={handleChange}
        className="sr-only peer"
      />
      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
    </label>
  );
}

function playCompletionSound(soundType: string) {
  try {
    const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioCtx.createOscillator();
    const gainNode = audioCtx.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(audioCtx.destination);
    gainNode.gain.value = 0.3;

    const sounds: Record<string, { freq: number; duration: number; type: OscillatorType }[]> = {
      chime: [
        { freq: 523, duration: 0.2, type: 'sine' },
        { freq: 659, duration: 0.2, type: 'sine' },
        { freq: 784, duration: 0.4, type: 'sine' },
      ],
      bell: [
        { freq: 440, duration: 0.6, type: 'sine' },
      ],
      celebration: [
        { freq: 523, duration: 0.15, type: 'square' },
        { freq: 659, duration: 0.15, type: 'square' },
        { freq: 784, duration: 0.15, type: 'square' },
        { freq: 1047, duration: 0.3, type: 'square' },
      ],
      gentle: [
        { freq: 392, duration: 0.5, type: 'sine' },
      ],
    };

    const notes = sounds[soundType] || sounds.chime;
    let time = audioCtx.currentTime;
    notes.forEach((note) => {
      const osc = audioCtx.createOscillator();
      const g = audioCtx.createGain();
      osc.connect(g);
      g.connect(audioCtx.destination);
      osc.type = note.type;
      osc.frequency.value = note.freq;
      g.gain.value = 0.3;
      g.gain.exponentialRampToValueAtTime(0.001, time + note.duration);
      osc.start(time);
      osc.stop(time + note.duration);
      time += note.duration;
    });
  } catch (err) {
    console.warn('[playCompletionSound] Audio failed:', err);
  }
}

export default function HubTimersFixed() {
  const { user } = useAuth0();
  const userId = user?.sub || '';
  const [, setLocation] = useLocation();
  const { currentHubData, hubId: hookHubId, loading: hubLoading } = useCurrentHub();
  
  // Use hubId directly from hook, fallback to currentHubData
  const hubId = hookHubId || currentHubData?.hub?.id || '';
  const hubName = currentHubData?.hub?.name || 'My Hub';
  const { canWrite } = useHubWriteAccess(hubId || undefined);
  
  // Debug logging for hub state
  useEffect(() => {
    console.log('[HubTimersFixed] Hub state:', { hubId, hookHubId, hubLoading, hasHubData: !!currentHubData });
  }, [hubId, hookHubId, hubLoading, currentHubData]);
  
  // All hooks must be called before any conditional returns
  const [activeTab, setActiveTab] = useState<'circular' | 'rectangular'>('circular');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingTimer, setEditingTimer] = useState<any>(null);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [activeTimerType, setActiveTimerType] = useState<'circular' | 'rectangular'>('circular');
  const [selectedShareResource, setSelectedShareResource] = useState<ResourceDescriptor | null>(null);
  const [selectedShareCanDirectly, setSelectedShareCanDirectly] = useState(false);
  const [isStartingTimer, setIsStartingTimer] = useState(false);
  const { toast } = useToast();
  const fetchWithAuth = useFetchWithAuth();
  
  // BroadcastChannel helper for care view synchronization
  const broadcastTimerUpdate = (timerId: string | number, state?: { status?: string; endsAtMs?: number; remainingSeconds?: number }) => {
    try {
      const channel = new BroadcastChannel('timer-updates');
      const payload = { 
        type: 'timer-update', 
        timerId, 
        hubId,
        status: state?.status,
        endsAtMs: state?.endsAtMs,
        remainingSeconds: state?.remainingSeconds
      };
      channel.postMessage(payload);
      channel.close();
      console.log('[HubTimersFixed] Broadcast timer update:', payload);
    } catch (err) {
      console.warn('[HubTimersFixed] BroadcastChannel failed:', err);
    }
  };
  
  // Subscribe to Zustand timer store for rendering - this drives UI updates from WebSocket
  const timerStoreTimers = useTimerStore((state) => state.timers);
  
  // Guard for one-time auto-open after hydration only
  const hasAutoOpenedRef = useRef(false);
  
  // Guard to distinguish user-initiated Start from hydration
  const userStartedTimerRef = useRef(false);
  
  // Track when initial data load is complete - prevents modal from opening during loading
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  
  // PART 5: Reset dialog state on initial mount - ensure form is closed
  useEffect(() => {
    setEditingTimer(null);
    setIsFormOpen(false);
    hasAutoOpenedRef.current = false;
  }, []);
  
  // Mark initial load complete when hub and timers are ready
  useEffect(() => {
    if (hubId && !hubLoading) {
      // Delay slightly to ensure all hydration is done
      const timer = setTimeout(() => {
        setInitialLoadComplete(true);
        console.log('[HubTimersFixed] Initial load complete');
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [hubId, hubLoading]);
  
  // Debug: Log when timer store changes to verify re-renders
  useEffect(() => {
    const timerIds = Object.keys(timerStoreTimers);
    const runningTimers = Object.values(timerStoreTimers).filter(t => t.status === 'running' || t.isRunning);
    console.log('[HubTimersFixed] timerStoreTimers updated:', {
      count: timerIds.length,
      running: runningTimers.length,
      timers: timerStoreTimers
    });
  }, [timerStoreTimers]);
  
  // Initialize WebSocket and set hubId for timer store when hub is ready
  useEffect(() => {
    if (hubId) {
      window.__currentHubId = hubId;
      const { initWebSocket } = useTimerStore.getState();
      initWebSocket();
    }
  }, [hubId]);

  // Hydrate timer state from active session on page load/refresh
  useEffect(() => {
    if (!hubId) return;

    const hydrateFromActiveSession = async () => {
      try {
        const response = await fetchWithAuth(`/api/timer-sessions/active?hubId=${hubId}`);
        
        if (!response.ok) {
          console.log('[HubTimersFixed] Active session request failed:', response.status);
          return;
        }
        
        // Parse JSON response
        const session = await response.json();
        
        if (!session || !session.timerId) {
          console.log('[HubTimersFixed] No active session to hydrate');
          return;
        }

        console.log('[HubTimersFixed] Hydrating from active session:', session);
        
        const store = useTimerStore.getState();
        // seedFromSession now calls hydrateRunningTimer internally if timer is running
        store.seedFromSession(session);
      } catch (error) {
        console.error('[HubTimersFixed] Error hydrating active session:', error);
      }
    };

    hydrateFromActiveSession();
  }, [hubId, fetchWithAuth]);

  // Load timers from database
  const { data: timers = [], isLoading, refetch } = useQuery<any[]>({
    queryKey: ['/api/timers', { hubId }],
    enabled: !!hubId,
  });

  const { data: schedulesData = [] } = useQuery<any[]>({
    queryKey: ['/api/schedules/recent', { hubId, limit: 50 }],
    enabled: !!hubId,
  });

  const { data: narrativesData = [] } = useQuery<any[]>({
    queryKey: ['/api/social-narratives', { hubId }],
    enabled: !!hubId,
  });

  const availableResources = [
    ...schedulesData.map((s: any) => ({ id: s.id, title: s.title || s.name || 'Untitled Schedule', type: 'schedule' as const })),
    ...narrativesData.map((n: any) => ({ id: n.id, title: n.title || 'Untitled Story', type: 'narrative' as const })),
  ];

  const [completionOverlay, setCompletionOverlay] = useState<{
    timerId: string;
    completionKey: string;
    timerTitle: string;
    completionSound: string | null;
    completionResourceType: string | null;
    completionResourceId: string | null;
  } | null>(null);

  const completedTimerIdsRef = useRef<Set<string>>(new Set());
  const pendingCompletionsRef = useRef<Map<string, any>>(new Map());

  useEffect(() => {
    const showCompletion = (detail: any, timerData: any, completionKey: string) => {
      acknowledgeTimerCompletion(completionKey);
      const sound = timerData.completionSound;
      if (sound && sound !== 'none') {
        playCompletionSound(sound);
      }

      try {
        import('canvas-confetti').then((mod) => {
          const confetti = mod.default;
          confetti({ particleCount: 150, spread: 80, origin: { y: 0.6 } });
        });
      } catch (err) {
        console.warn('[HubTimersFixed] Confetti failed:', err);
      }

      setCompletionOverlay({
        timerId: String(detail.timerId),
        completionKey,
        timerTitle: timerData.title || timerData.name || 'Timer',
        completionSound: timerData.completionSound || null,
        completionResourceType: timerData.completionResourceType || null,
        completionResourceId: timerData.completionResourceId || null,
      });
    };

    const handleTimerCompleted = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const { timerId } = detail;
      const key = String(timerId);
      const completionKey = getTimerCompletionKey(detail);
      if (!completionKey || hasAcknowledgedTimerCompletion(completionKey)) return;
      if (completedTimerIdsRef.current.has(completionKey)) return;
      completedTimerIdsRef.current.add(completionKey);

      const timerData = timers.find((t: any) => String(t.id) === key || String(t.timerId) === key);
      if (!timerData) {
        pendingCompletionsRef.current.set(completionKey, detail);
        return;
      }

      showCompletion(detail, timerData, completionKey);
    };

    window.addEventListener('timer-completed', handleTimerCompleted);
    for (const [completionKey, detail] of pendingCompletionsRef.current) {
      const timerData = timers.find(
        (t: any) => String(t.id) === String(detail.timerId) || String(t.timerId) === String(detail.timerId),
      );
      if (timerData) {
        pendingCompletionsRef.current.delete(completionKey);
        showCompletion(detail, timerData, completionKey);
      }
    }
    return () => window.removeEventListener('timer-completed', handleTimerCompleted);
  }, [timers]);

  // Auto-open TimerForm ONCE after hydration - ONLY for paused timers, NOT running
  // DISABLED: This was causing unexpected modal opens. User must manually open form.
  useEffect(() => {
    // Only auto-open ONCE after hydration - not on every store update
    if (hasAutoOpenedRef.current) return;
    
    // CRITICAL: Wait for initial load to complete before considering auto-open
    if (!initialLoadComplete) return;
    
    // Skip if user just clicked Start - this is not a hydration event
    if (userStartedTimerRef.current) return;

    // Mark as done after initial check - prevents any future auto-opens
    // We're disabling auto-open to fix the modal issue
    hasAutoOpenedRef.current = true;
    console.log('[HubTimersFixed] Auto-open disabled - user must manually open form');
    
    // NOTE: Previous auto-open logic for paused timers removed to prevent unexpected modal opens
  }, [timerStoreTimers, isFormOpen, initialLoadComplete]);

  // Filter timers by type
  const circularTimers = timers.filter((timer: any) => !timer.segments || timer.segments.length === 0);
  const rectangularTimers = timers.filter((timer: any) => timer.segments && timer.segments.length > 0);

  // Derived readiness check for timer start
  const isAppReadyForTimerStart = Boolean(hubId) && !hubLoading && !isLoading;

  // PART 1: Guard for timer mutations - must have hubId and not loading
  const canMutateTimers = !!hubId && !hubLoading;

  // Create timer mutation
  const createTimerMutation = useMutation({
    mutationFn: (timerData: any) => {
      if (!hubId) {
        throw new Error('Hub ID not available');
      }
      const payload: any = {
        name: timerData.name,
        durationSeconds: timerData.durationSeconds,
        image: timerData.image || null,
        hubId: hubId,
        type: timerData.type || 'circular',
      };
      if (timerData.segments && timerData.segments.length > 0) {
        payload.segments = timerData.segments;
      }
      if (timerData.completionSound !== undefined) payload.completionSound = timerData.completionSound;
      if (timerData.completionResourceType !== undefined) payload.completionResourceType = timerData.completionResourceType;
      if (timerData.completionResourceId !== undefined) payload.completionResourceId = timerData.completionResourceId;
      console.log('[CreateTimer] payload:', payload);
      return apiRequest(`/api/timers?hubId=${hubId}`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/timers', { hubId }] });
      setIsFormOpen(false);
      setEditingTimer(null);
      toast({
        title: "Success",
        description: "Timer created successfully!",
      });
    },
    onError: (error) => {
      console.error('Error creating timer:', error);
      toast({
        title: "Error",
        description: "Failed to create timer. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Update timer mutation
  const updateTimerMutation = useMutation({
    mutationFn: async ({ timerId, timerData }: { timerId: string | number; timerData: any }) => {
      const payload: any = {
        hubId: hubId,
      };
      // Only include fields that are provided
      if (timerData.name !== undefined) payload.name = timerData.name;
      if (timerData.durationSeconds !== undefined) payload.durationSeconds = timerData.durationSeconds;
      if (timerData.image !== undefined) payload.image = timerData.image || null;
       if (timerData.segments !== undefined) payload.segments = timerData.segments;
      if (timerData.showInCareView !== undefined) payload.showInCareView = timerData.showInCareView;
      if (timerData.completionSound !== undefined) payload.completionSound = timerData.completionSound;
      if (timerData.completionResourceType !== undefined) payload.completionResourceType = timerData.completionResourceType;
      if (timerData.completionResourceId !== undefined) payload.completionResourceId = timerData.completionResourceId;

      // ── Diagnostic logging ─────────────────────────────────────────────
      const imageField = payload.image ?? '(none)';
      const isBase64 = typeof imageField === 'string' && imageField.startsWith('data:');
      console.log(`[updateTimer] timerId=${timerId} image="${imageField.toString().substring(0, 60)}${imageField.length > 60 ? '…' : ''}" isBase64=${isBase64}`);
      if (isBase64) {
        console.error('[updateTimer] ⚠️  payload.image is STILL base64 — this will trigger a WAF 403. Clearing it to prevent the block.');
        payload.image = null;
      }
      // ──────────────────────────────────────────────────────────────────

      return apiRequest(`/api/timers/${timerId}/save`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },
    onSuccess: (updatedTimer: any, variables: { timerId: string | number; timerData: any }) => {
      // Directly update Zustand store with new duration so the display refreshes immediately.
      // The sync effect skips running timers (hasActiveInterval guard), so we bypass it here.
      if (updatedTimer?.durationSeconds !== undefined) {
        const store = useTimerStore.getState();
        const existing = store.timers[variables.timerId];
        const serverIsRunning = updatedTimer.status === 'running' || updatedTimer.isRunning === true;
        const isActivelyRunning = serverIsRunning &&
          existing?.isRunning === true &&
          existing?.isPaused !== true;
        store.updateTimerState(variables.timerId, {
          duration: updatedTimer.durationSeconds,
          ...(isActivelyRunning
            ? {}
            : {
                // A paused/not-started edit is a fresh configuration, not a resume.
                timeRemaining: updatedTimer.durationSeconds,
                status: 'paused',
                isRunning: false,
                isPaused: true,
                startedAt: undefined,
                endsAtMs: undefined,
                sessionId: undefined,
                completionKey: undefined,
              }),
        });
      }
      queryClient.invalidateQueries({ queryKey: ['/api/timers', { hubId }] });
      setIsFormOpen(false);
      setEditingTimer(null);
      toast({
        title: "Success",
        description: "Timer updated successfully!",
      });
    },
    onError: (error) => {
      console.error('Error updating timer:', error);
      toast({
        title: "Error",
        description: "Failed to update timer. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Delete timer mutation
  const deleteTimerMutation = useMutation({
    mutationFn: (timerId: string | number) => apiRequest(`/api/timers/${timerId}/remove`, {
      method: 'POST',
      body: JSON.stringify({ hubId }),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/timers', { hubId }] });
      toast({
        title: "Success",
        description: "Timer deleted successfully!",
      });
    },
    onError: (error) => {
      console.error('Error deleting timer:', error);
      toast({
        title: "Error",
        description: "Failed to delete timer. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Sync timers from API to Zustand store when timers change
  // CRITICAL: Also restart countdown intervals for running timers (e.g. after page refresh)
  useEffect(() => {
    // Log what the API returned for debugging - distinguish initial vs actual data
    const isActualData = !isLoading && timers.length > 0;
    const isEmptyResponse = !isLoading && timers.length === 0;
    console.log('[TIMERS API]', {
      stage: isLoading ? 'LOADING' : (timers.length > 0 ? 'DATA_RECEIVED' : 'EMPTY_RESPONSE'),
      count: timers.length,
      isLoading,
      hubId,
      timers: timers.map((t: any) => ({
        id: t.id,
        status: t.status,
        isPaused: t.isPaused,
        isRunning: t.isRunning,
        durationSeconds: t.durationSeconds,
      }))
    });
    
    if (timers.length > 0) {
      const store = useTimerStore.getState();
      timers.forEach((timer: any) => {
        const timerId = timer.id; // Use string ID directly (UUIDs are strings)
        if (!timerId) {
          console.warn('[TIMER DROPPED] No timer ID:', { timer });
          return;
        }
        
        // Check if timer is already running in store with active interval
        const existing = store.timers[timerId];
        const hasActiveInterval = existing?.isRunning && !existing?.isPaused;
        
        // Skip if timer already has an active countdown - don't overwrite
        if (hasActiveInterval) {
          console.log('[TIMER SKIP] Already has active interval:', { id: timerId });
          return;
        }
        
        // CRITICAL: Calculate duration properly - API returns durationSeconds
        let durationSecs: number;
        if (timer.durationSeconds && timer.durationSeconds > 0) {
          durationSecs = timer.durationSeconds;
        } else {
          console.warn('[TIMER DROPPED] No valid durationSeconds:', {
            id: timer.id,
            status: timer.status,
            durationSeconds: timer.durationSeconds,
            endsAtMs: timer.endsAtMs,
            isPaused: timer.isPaused,
          });
          return;
        }
        
        // Calculate remaining time - use startedAt if timer was running
        let remainingSecs: number;
        
        // CRITICAL FIX: Enforce mutual exclusivity of running/paused states
        // A timer cannot be both running AND paused - paused takes precedence
        // Use nullish coalescing to handle undefined values from server
        const isPaused = timer.isPaused ?? (timer.status === 'paused') ?? false;
        const serverIndicatesRunning = ((timer.isRunning ?? false) || timer.status === 'running') && !isPaused;
        
        // Log the derived state for debugging
        console.log('[Sync] Timer state derivation:', {
          timerId,
          rawStatus: timer.status,
          rawIsPaused: timer.isPaused,
          rawIsRunning: timer.isRunning,
          derivedIsPaused: isPaused,
          derivedIsRunning: serverIndicatesRunning,
        });
        
        if (serverIndicatesRunning && timer.startedAt) {
          // Timer was running - calculate elapsed time from startedAt
          const startedAtMs = typeof timer.startedAt === 'string' 
            ? new Date(timer.startedAt).getTime() 
            : timer.startedAt;
          const elapsedMs = Date.now() - startedAtMs;
          const elapsedSecs = Math.floor(elapsedMs / 1000);
          remainingSecs = Math.max(0, durationSecs - elapsedSecs);
          console.log('[Sync] Running timer - calculated remaining:', {
            timerId,
            startedAt: timer.startedAt,
            elapsedSecs,
            remainingSecs,
          });
        } else if (timer.remainingDuration) {
          remainingSecs = Math.floor(timer.remainingDuration / 1000);
        } else {
          remainingSecs = durationSecs;
        }

        // The calculated remaining time is authoritative. A stale server
        // status must not recreate a running timer after its end time.
        const isExpiredRunning = serverIndicatesRunning && remainingSecs <= 0;
        const isTimerRunning = serverIndicatesRunning && remainingSecs > 0;
        
        // Determine if this is a rectangular timer (has segments)
        const isRectangular = timer.type === 'rectangular' || (timer.segments && timer.segments.length > 0);
        
        console.log('[Sync] Seeding timer to store:', {
          timerId,
          durationSecs,
          remainingSecs,
          isTimerRunning,
          isPaused,
          image: timer.image,
          type: timer.type,
          isRectangular,
          segmentsCount: timer.segments?.length || 0,
        });
        
        // Update timer state with consistent, mutually exclusive states
        // Derive status from isPaused and isTimerRunning to ensure consistency
        const derivedStatus = isPaused
          ? 'paused'
          : isExpiredRunning
            ? 'stopped'
            : isTimerRunning
              ? 'running'
              : 'idle';
        
        // CRITICAL FIX: Clear endsAtMs for paused timers to prevent time-based countdown
        // A paused timer with endsAtMs will still count down because remainingTime = endsAtMs - now
        const safeEndsAtMs = isPaused ? undefined : (timer.endsAtMs || undefined);
        
        // Safety assertion - log if we ever see this invariant violated
        if (isPaused && timer.endsAtMs) {
          console.warn('[TimerInvariant] Paused timer has endsAtMs! Clearing it:', timerId);
        }
        
        store.updateTimerState(timerId, {
          id: timerId,
          duration: durationSecs,
          timeRemaining: remainingSecs,
          isRunning: isTimerRunning,
          isPaused,
          // Keep an expired running report recognizable to updateTimerState so
          // it emits the canonical completion occurrence while normalizing the
          // stored result to stopped.
          status: isExpiredRunning ? 'running' : derivedStatus,
          title: timer.name,
          hubId: hubId,
          image: timer.image,
          type: timer.type || (isRectangular ? 'rectangular' : 'circular'),
          segments: timer.segments,
          endsAtMs: safeEndsAtMs, // CRITICAL: null for paused timers
          startedAt: timer.startedAt,
          sessionId: timer.sessionId,
          completionKey: timer.sessionId
            ? `session:${timer.sessionId}`
            : (typeof timer.endsAtMs === 'number'
              ? `ends:${timer.endsAtMs}`
              : (timer.startedAt !== undefined ? `started:${String(timer.startedAt)}` : undefined)),
        });
        
        console.log('[TIMER ADDED] Successfully added to store:', {
          id: timerId,
          status: derivedStatus,
          isPaused,
          isRunning: isTimerRunning,
          duration: durationSecs,
          timeRemaining: remainingSecs,
        });
        
        // CRITICAL: Hydrate countdown ONLY for truly running timers (not paused)
        // isTimerRunning is already false for paused timers due to the fix above
        if (isTimerRunning && remainingSecs > 0) {
          // Check if already ticking - don't re-hydrate
          if (!store.isTicking(timerId)) {
            // Calculate endsAtMs from startedAt + duration
            const startedAtMs = typeof timer.startedAt === 'string' 
              ? new Date(timer.startedAt).getTime() 
              : timer.startedAt;
            const endsAtMs = startedAtMs ? startedAtMs + (durationSecs * 1000) : Date.now() + (remainingSecs * 1000);
            
            console.log('[Sync] Hydrating running timer from server:', {
              timerId,
              endsAtMs,
              isRectangular,
            });
            
            // Hydrate purely from endsAtMs - no duration parameter
            store.hydrateRunningTimer(timerId, endsAtMs);
          } else {
            console.log('[Sync] Timer already ticking, skip hydration:', timerId);
          }
        }
      });
    }
  }, [timers, hubId, isLoading]);
  
  // Format time helper - supports HH:MM:SS for timers over 1 hour
  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    // Return HH:MM:SS if >= 1 hour, otherwise MM:SS
    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Timer management functions
  const handleCreateTimer = () => {
    setEditingTimer(null);
    setActiveTimerType('circular');
    setIsFormOpen(true);
  };

  const handleCreateRectangularTimer = () => {
    console.log('🟩 Creating rectangular timer - before state updates');
    setEditingTimer(null);
    setActiveTimerType('rectangular');
    setIsFormOpen(true);
    console.log('🟩 Creating rectangular timer - after state updates');
  };
  
  const handleEditTimer = (timer: any) => {
    // Guard: only allow edit if hub is ready
    if (!canMutateTimers) {
      console.warn('[Timer] Edit blocked - hub not ready');
      return;
    }
    setEditingTimer(timer);
    // Set the timer type based on the timer being edited
    setActiveTimerType(timer.timerShape === 'rectangular' ? 'rectangular' : 'circular');
    setIsFormOpen(true);
  };
  
  const handleStartTimer = async (timer: any) => {
    // Hard guard: block if app state is not fully ready
    if (!isAppReadyForTimerStart || !timer?.id) {
      console.warn('[Timer] Start blocked – app not ready', {
        hubId,
        hubLoading,
        isLoading,
        timerId: timer?.id
      });
      return;
    }
    
    // Block if already starting a timer
    if (isStartingTimer) {
      console.warn('[Timer] Start blocked – already starting a timer');
      return;
    }
    
    // Mark that user initiated this action - prevents auto-open from firing
    userStartedTimerRef.current = true;
    
    console.log('🔴 Starting timer:', timer.id, 'Full timer object:', timer);
    
    setIsStartingTimer(true);
    
    try {
      console.log('🔴 Making API request to:', `/api/timers/${timer.id}/start`);
      const response = await fetchWithAuth(`/api/timers/${timer.id}/start`, {
        method: 'POST',
      });
      if (!response.ok) {
        throw new Error(`Timer start failed: ${response.status}`);
      }
      const startSession = await response.json();
      console.log('🔴 API request successful:', startSession);
      
      // Refresh timers to get updated status
      refetch();
      
      // Use timer store for local countdown - startTimer handles everything
      try {
        const store = useTimerStore.getState();
        const timerId = timer.id; // Use string ID directly (UUIDs are strings)
        
        // CRITICAL: Get duration from timer record - API returns durationSeconds
        let durationSecs: number;
        if (timer.durationSeconds && timer.durationSeconds > 0) {
          durationSecs = timer.durationSeconds;
        } else {
          console.error('[HubTimersFixed] Timer has no valid duration:', timer);
          toast({
            title: "Error",
            description: "Timer duration is missing. Please edit the timer.",
            variant: "destructive",
          });
          setIsStartingTimer(false);
          return;
        }
        
        console.log('[HubTimersFixed] Timer duration calculation:', {
          durationSeconds: timer.durationSeconds,
          calculatedDurationSecs: durationSecs,
          image: timer.image,
        });
        
        const existingTimer = store.timers[timerId];
        const remainingSecs = existingTimer?.timeRemaining ?? durationSecs;
        
        // First ensure timer state exists with correct values
        store.updateTimerState(timerId, {
          id: timerId,
          duration: durationSecs,
          timeRemaining: remainingSecs,
          isRunning: false,
          isPaused: false,
          status: 'idle',
          title: timer.name,
          hubId: hubId,
          image: timer.image,
          sessionId: startSession.sessionId,
          completionKey: startSession.sessionId ? `session:${startSession.sessionId}` : undefined,
          lastUpdated: Date.now(),
        });
        
        // Then start the timer - this sets isRunning and starts the countdown interval
        await store.startTimer(timerId);
        
        console.log('[HubTimersFixed] Timer started in store:', timerId, 'duration:', durationSecs);
      } catch (error) {
        console.warn('Timer store start failed:', error);
      }
      
      toast({
        title: "Timer Started",
        description: `${timer.name} is now running`,
      });
      
      // Broadcast so care view refetches with new endsAt
      // Get the timer state from store to get endsAtMs
      const storeState = useTimerStore.getState();
      const timerState = storeState.timers[timer.id];
      const durationSecs = timer.durationSeconds || timer.duration || 300;
      const endsAtMs = timerState?.endsAt ? new Date(timerState.endsAt).getTime() : Date.now() + (durationSecs * 1000);
      
      broadcastTimerUpdate(timer.id, {
        status: 'running',
        endsAtMs,
        remainingSeconds: timerState?.timeRemaining ?? durationSecs
      });
      
      setIsStartingTimer(false);
      
    } catch (error) {
      console.error('🔴 Error starting timer:', error);
      toast({
        title: "Error",
        description: `Failed to start timer: ${error}`,
        variant: "destructive",
      });
      setIsStartingTimer(false);
    }
  };

  const handleStopTimer = async (timerId: string) => {
    console.log('Stopping timer:', timerId);
    
    try {
      // Call API to pause timer
      await fetchWithAuth(`/api/timers/${timerId}/pause`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      
      // Refresh timers to get updated status
      refetch();
      
      // Use timer store to sync across devices
      const numericId = typeof timerId === 'string' ? parseInt(timerId, 10) : timerId;
      const { resetTimer } = useTimerStore.getState();
      resetTimer(numericId);
      
      // Broadcast paused state
      broadcastTimerUpdate(timerId, { status: 'paused' });
      
    } catch (error) {
      console.error('Error stopping timer:', error);
    }
  };

  const handlePauseTimer = async (timerId: number | string) => {
    console.log('[PAUSE] Starting pause for timer:', timerId);
    
    try {
      // CRITICAL: Call the backend API FIRST to persist pause state
      const response = await fetchWithAuth(`/api/timers/${timerId}/pause`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[PAUSE] Backend error:', response.status, errorText);
        throw new Error(`Failed to pause timer: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('[PAUSE] Backend pause successful:', result);
      
      // NOW update local store after backend confirms
      const { pauseTimer } = useTimerStore.getState();
      await pauseTimer(timerId);
      
      // Refresh timers to get updated status from server
      refetch();
      
      // Broadcast so care view refetches with updated state
      const storeState = useTimerStore.getState();
      const timerState = storeState.timers[timerId];
      broadcastTimerUpdate(timerId, { 
        status: 'paused',
        remainingSeconds: timerState?.timeRemaining
      });
      
    } catch (error) {
      console.error('[PAUSE] Error pausing timer:', error);
    }
  };

  const handleResumeTimer = async (timerId: number | string) => {
    console.log('[RESUME] Starting resume for timer:', timerId);
    
    try {
      // CRITICAL: Call the backend API FIRST to persist resume state
      const response = await fetchWithAuth(`/api/timers/${timerId}/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[RESUME] Backend error:', response.status, errorText);
        throw new Error(`Failed to resume timer: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('[RESUME] Backend resume successful:', result);
      
      // Extract endsAtMs from server response for accurate sync
      const endsAtMs = result.endsAtMs || result.session?.endsAtMs || 
        (result.session?.endsAt ? new Date(result.session.endsAt).getTime() : Date.now() + 300000);
      
      // NOW update local store after backend confirms
      const { startTimer, hydrateRunningTimer } = useTimerStore.getState();
      await startTimer(timerId);
      
      // Use server's endsAtMs for accurate countdown
      hydrateRunningTimer(timerId, endsAtMs);
      
      // Refresh timers to get updated status from server
      refetch();
      
      // Broadcast so care view refetches with new endsAt
      const storeState = useTimerStore.getState();
      const timerState = storeState.timers[timerId];
      broadcastTimerUpdate(timerId, {
        status: 'running',
        endsAtMs,
        remainingSeconds: timerState?.timeRemaining
      });
      
    } catch (error) {
      console.error('[RESUME] Error resuming timer:', error);
    }
  };

  const handleRestartTimer = async (timer: any) => {
    try {
      // Reset timer to initial duration
      await fetchWithAuth(`/api/timers/${timer.id}/reset`, {
        method: 'POST',
      });
      
      // Refresh timers
      refetch();
      
      // Broadcast so care view refetches with reset state
      const durationSecs = timer.durationSeconds || timer.duration || 300;
      broadcastTimerUpdate(timer.id, { 
        status: 'paused',
        remainingSeconds: durationSecs
      });
      
      toast({
        title: "Timer Reset",
        description: `${timer.name} has been reset`,
      });
    } catch (error) {
      console.error('Error resetting timer:', error);
      toast({
        title: "Error",
        description: "Failed to reset timer",
        variant: "destructive",
      });
    }
  };

  const handleDeleteTimer = (timerId: number) => {
    // Guard: only allow delete if hub is ready
    if (!canMutateTimers) {
      console.warn('[Timer] Delete blocked - hub not ready');
      return;
    }
    if (confirm('Are you sure you want to delete this timer?')) {
      // Stop the timer if it's running in the store
      const timerState = timerStoreTimers[timerId];
      if (timerState?.isRunning) {
        handleStopTimer(String(timerId));
      }
      
      // Clear from store
      const { clearTimer } = useTimerStore.getState();
      clearTimer(timerId);
      
      // Delete using database mutation
      deleteTimerMutation.mutate(timerId);
    }
  };

  const handleToggleCareView = (timer: any, showInCareView: boolean) => {
    updateTimerMutation.mutate({
      timerId: timer.id,
      timerData: { 
        showInCareView,
        image: timer.image ?? undefined  // Preserve existing image
      }
    }, {
      onSuccess: () => {
        // Invalidate cache so toggle UI updates
        queryClient.invalidateQueries({ queryKey: ['/api/timers', { hubId }] });
        
        // Broadcast so care view knows to refetch
        try {
          const channel = new BroadcastChannel('timer-updates');
          channel.postMessage({ 
            type: 'timer-visibility-change', 
            timerId: timer.id, 
            hubId,
            showInCareView 
          });
          channel.close();
        } catch {
          // BroadcastChannel not supported
        }
      }
    });
  };

  const handleShareTimer = (timer: any) => {
    const durationSeconds = timer.durationSeconds || 0;
    setSelectedShareCanDirectly(timer.canDirectShare === true);
    setSelectedShareResource({
      type: 'timer',
      id: String(timer.id),
      title: timer.name,
      description: `Timer duration: ${formatTime(durationSeconds)}`,
      imageUrl: timer.image || null,
      tags: ['timer', 'productivity', 'organization'],
      metadata: { durationSeconds },
    });
  };
  
  const handleSaveTimer = (timerData: any) => {
    console.log('🔴 handleSaveTimer called with data:', timerData);
    
    // Guard: only allow save if hub is ready
    if (!canMutateTimers) {
      console.error('🔴 Cannot save timer: hub not ready (canMutateTimers=false)');
      toast({
        title: "Error",
        description: "Hub not loaded. Please wait and try again.",
        variant: "destructive",
      });
      return;
    }
    
    if (editingTimer) {
      console.log('🔴 Updating timer:', editingTimer.id);
      updateTimerMutation.mutate({ 
        timerId: editingTimer.id, 
        timerData: timerData 
      });
    } else {
      console.log('🔴 Creating new timer');
      createTimerMutation.mutate(timerData);
    }
  };
  
  const handleCancelForm = () => {
    setIsFormOpen(false);
    setEditingTimer(null);
  };

  // Show loading state ONLY if we have no hubId at all
  // Don't block on hubLoading if we already have a hubId
  if (!hubId) {
    console.log('[HubTimersFixed] Showing loading - no hubId yet');
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <div className="container mx-auto px-4 sm:px-6 py-8 max-w-6xl flex-grow">
          <div className="text-center py-12">Loading timers...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen" data-testid="timers-page">
      <Header />
      <HaasTooltip storageKey={FEATURE_TOOLTIPS.timer.key} message={FEATURE_TOOLTIPS.timer.message} />
      
      <div className="container mx-auto px-4 sm:px-6 py-8 max-w-6xl flex-grow">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-primary">Visual Timers</h1>
            <p className="text-gray-600 mt-1">Timers that make transitions easier to understand</p>
          </div>
          <Link href={`/hub/${hubId}`}>
            <Button variant="outline">Back to Hub</Button>
          </Link>
        </div>
        
        <div className="mb-6">
          <div className="flex border-b mb-4">
            <button 
              onClick={() => setActiveTab('circular')}
              className={`px-4 py-2 font-medium border-b-2 ${
                activeTab === 'circular' 
                  ? 'border-blue-600 text-blue-600' 
                  : 'border-transparent text-gray-600 hover:text-blue-600'
              }`}
            >
              Circular Timers
            </button>
            <button 
              onClick={() => setActiveTab('rectangular')}
              className={`px-4 py-2 font-medium border-b-2 ${
                activeTab === 'rectangular' 
                  ? 'border-blue-600 text-blue-600' 
                  : 'border-transparent text-gray-600 hover:text-blue-600'
              }`}
            >
              Rectangular Timers
            </button>
          </div>
          
          {activeTab === 'circular' && (
            <div>
              {canWrite && <div className="flex justify-end mb-4">
                <button 
                  onClick={handleCreateTimer}
                  disabled={!hubId}
                  data-testid="create-timer-button"
                  className={`px-4 py-2 rounded-md font-medium transition-colors ${
                    !hubId 
                      ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  New Timer
                </button>
              </div>}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {timers.filter((timer: any) => timer.timerShape === 'circular').map((timer: any) => {
                  const timerState = timerStoreTimers[timer.id];
                  // Timer is only "active" if it has been started before (has startedAt from SERVER data)
                  // A timer with status='paused' and no startedAt is "idle" - show Start, not Resume
                  // Use ONLY timer (server) data for startedAt - do not use store
                  const hasBeenStarted = !!(timer.startedAt || timer.startTime);
                  // Use store for real-time running state, fallback to server status
                  const isRunning = timerState?.isRunning ?? (timer.status === 'running');
                  // Timer is only "paused" (show Resume) if it was previously started
                  const isPaused = hasBeenStarted && !isRunning && (timerState?.isPaused ?? timer.status === 'paused');
                  const isActive = isRunning || isPaused;
                  
                  // Use durationSeconds from API (already in seconds)
                  const durationSecs = timer.durationSeconds || 0;
                  const displayRemaining = isActive 
                    ? (timerState?.timeRemaining ?? durationSecs) 
                    : durationSecs;
                  
                  // Use store image if available, fallback to API image
                  const displayImage = timerState?.image || timer.image;
                  
                  return (
                  <div key={timer.id} className="border rounded-lg overflow-hidden shadow-sm hover:shadow-md" data-testid="timer-card">
                    <div className="relative flex justify-center items-center p-4">
                      {/* Always use CircularTimer - it handles both active and inactive states */}
                      <CircularTimer
                        image={displayImage}
                        totalDuration={durationSecs}
                        remainingTime={displayRemaining}
                        mode="cover"
                        size={200}
                        timerId={timer.id}
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-xl font-bold">{timer.name}</h3>
                      
                      {/* Timer Controls */}
                      {canWrite && <div className="flex justify-between items-center mt-4 gap-2">
                        {isActive ? (
                          <>
                            {isPaused ? (
                              <SimpleButton 
                                onClick={() => handleResumeTimer(timer.id)}
                                variant="outline"
                                data-testid="timer-resume"
                              >
                                Resume
                              </SimpleButton>
                            ) : (
                              <SimpleButton 
                                onClick={() => handlePauseTimer(timer.id)}
                                variant="outline"
                                data-testid="timer-pause"
                              >
                                Pause
                              </SimpleButton>
                            )}
                            <SimpleButton 
                              onClick={() => handleRestartTimer(timer)}
                              variant="outline"
                              data-testid="timer-restart"
                            >
                              Restart
                            </SimpleButton>
                          </>
                        ) : (
                          <SimpleButton onClick={() => handleStartTimer(timer)} data-testid="timer-start">Start</SimpleButton>
                        )}
                      </div>}
                      
                      {/* Care View Toggle and Action Icons */}
                      {canWrite && <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">Show in Care View:</span>
                          <CareViewToggle 
                            initialValue={timer.showInCareView || false}
                            onToggle={(checked) => handleToggleCareView(timer, checked)}
                          />
                        </div>
                        <div className="flex items-center gap-1 flex-wrap">
                          <AddToHubButton
                            resourceType="timer"
                            resourceId={String(timer.id)}
                            title={timer.name}
                            thumbnailUrl={timer.image}
                            size="sm"
                            variant="ghost"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleShareTimer(timer)}
                            className="p-1.5 h-7 w-7"
                            title="Share"
                          >
                            <Share2 className="w-4 h-4 text-blue-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditTimer(timer)}
                            className="p-1.5 h-7 w-7"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4 text-gray-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteTimer(timer.id)}
                            className="p-1.5 h-7 w-7"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                      </div>}
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {activeTab === 'rectangular' && (
            <div>
              {rectangularTimers.length === 0 ? (
                <div className="text-center py-12">
                  <h3 className="text-xl font-semibold mb-4">Rectangular Timers</h3>
                  <p className="text-gray-600 mb-6">Create step-by-step timers with multiple sections</p>
                  {canWrite && <button
                    onClick={handleCreateRectangularTimer}
                    disabled={!hubId}
                    className={`px-4 py-2 rounded-md font-medium transition-colors ${
                      !hubId 
                        ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    Create Rectangular Timer
                  </button>}
                </div>
              ) : (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-semibold">Rectangular Timers</h3>
                    {canWrite && <button
                      onClick={handleCreateRectangularTimer}
                      disabled={!hubId}
                      className={`px-4 py-2 rounded-md font-medium transition-colors ${
                        !hubId 
                          ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      Create Rectangular Timer
                    </button>}
                  </div>
                  <div className="space-y-4">
                    {timers.filter((timer: any) => timer.timerShape === 'rectangular').map((timer: any) => {
                      const timerState = timerStoreTimers[timer.id];
                      const timerWithLiveState = timerState ? {
                        ...timer,
                        isRunning: timerState.isRunning,
                        isPaused: timerState.isPaused,
                        timeRemaining: timerState.timeRemaining,
                        // CRITICAL: Preserve startedAt for hydration - prefer store, fallback to server
                        startedAt: timerState.startedAt ?? timer.startedAt,
                      } : timer;
                      
                      return (
                        <RectangularTimerFixed
                          key={timer.id}
                          timer={timerWithLiveState}
                          onToggleCareView={canWrite ? handleToggleCareView : undefined}
                          onShare={canWrite ? handleShareTimer : undefined}
                          onDelete={canWrite ? handleDeleteTimer : undefined}
                          onEdit={canWrite ? handleEditTimer : undefined}
                          viewOnly={!canWrite}
                          showControls={canWrite}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        
        <div style={{paddingBottom: '80px'}}></div>
      </div>
      
      <NavigationFooter hubId={hubId} />
      
      {canWrite && (
        <TimerForm
          timer={editingTimer}
          onSave={handleSaveTimer}
          onCancel={handleCancelForm}
          isOpen={isFormOpen}
          timerType={activeTimerType}
          hubId={hubId}
          availableResources={availableResources}
        />
      )}

      {canWrite && selectedShareResource && (
        <ResourceShareDialog
          open
          onOpenChange={(open) => {
            if (!open) {
              setSelectedShareResource(null);
              setSelectedShareCanDirectly(false);
            }
          }}
          hubId={hubId}
          resource={selectedShareResource}
          canDirectShare={selectedShareCanDirectly}
        />
      )}

      {completionOverlay && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-2xl p-8 max-w-sm w-full mx-4 text-center shadow-2xl">
            <div className="text-5xl mb-4">🎉</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">All Done!</h2>
            <p className="text-gray-600 mb-6">{completionOverlay.timerTitle} has finished</p>

            <div className="space-y-3">
              {isValidTimerCompletionResource(
                completionOverlay.completionResourceType,
                completionOverlay.completionResourceId,
              ) && (
                <Button
                  className="w-full"
                  onClick={() => {
                    const type = completionOverlay.completionResourceType;
                    const id = completionOverlay.completionResourceId;
                    acknowledgeTimerCompletion(completionOverlay.completionKey);
                    setCompletionOverlay(null);
                    const path = getNormalTimerCompletionPath(hubId, type, id);
                    if (path) {
                      setLocation(path);
                    }
                  }}
                >
                  {completionOverlay.completionResourceType === 'schedule' ? '📋 Open Schedule' : '📖 Open Story'}
                </Button>
              )}

              <Button
                variant="ghost"
                className="w-full text-gray-500"
                onClick={() => {
                  acknowledgeTimerCompletion(completionOverlay.completionKey);
                  setCompletionOverlay(null);
                }}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}